import { toaster } from 'evergreen-ui';
import { useState, useCallback, useEffect, useMemo } from 'react';

interface ValidationResult {
    isValid: boolean;
    statusMessage?: string;
};

export type FieldValidationResults<T> = {
    [K in keyof T]?: ValidationResult;
};

export interface FieldConfig<T, K extends keyof T = keyof T> {
    name: K;
    required?: boolean;
    /** 
     * Custom validation function for this field.
     * @param value - The current value of this field
     * @param formValues - All form values (optional, use when validation depends on other fields)
     */
    validate?: (value: T[K], formValues?: T) => ValidationResult;
}

export interface SubmitOperationConfig {
    loading: boolean;
    called: boolean;
    error: any;
    successMessage: string;
    failureMessage: string;
    onSuccess?: () => void;
    successDescription?: string; // Optional description for success
};

export type FormConfig<T> = {
    fields: Array<FieldConfig<T, keyof T>>;
    validateForm?: (formValues: T, validationResults: FieldValidationResults<T>) => ValidationResult;
    submitOperationConfig: SubmitOperationConfig;
};

export interface GenericFormReturn<T> {
    formValues: T;

    isFieldValid: <K extends keyof T>(fieldName: K) => boolean;
    setField: <K extends keyof T>(fieldName: K, value: T[K]) => void;
    resetForm: () => void;

    formStatusMessage: string;
    isFormValid: boolean;
    isFormDirty: boolean;
}

export interface GenericFormProps<T> {
    initialValues: T;
    initialFormStatusMessage?: string;
    formConfig: FormConfig<T>;
}

export const MISSING_REQUIRED_INFORMATION = "Missing required information";

/**
 * Generic form hook for managing form state, validation, and submission.
 * 
 * This hook provides a standardized way to handle form logic across the application,
 * including field validation, form state management, and submission handling.
 * 
 * @param initialValues - Initial values for the form fields
 * @param initialFormStatusMessage - Initial status message for the form
 * @param formConfig - Configuration for the form, including fields and validation logic
 * @returns An object containing form state and methods for managing the form
 */
export function useGenericForm<T extends Record<string, any>>({
    initialValues,
    initialFormStatusMessage = "",
    formConfig,
}: GenericFormProps<T>): GenericFormReturn<T> {
    const [formValues, setFormValues] = useState<T>(initialValues);
    const [formStatusMessage, setFormStatusMessage] = useState<string>(initialFormStatusMessage);
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false);

    const validateField = useCallback(
        <K extends keyof T>(fieldConfig: FieldConfig<T, K>, value: T[K], allValues: T): ValidationResult => {
            if (isFormDirty) {
                // Check required fields
                if (fieldConfig.required && !value) {
                    return {
                        isValid: false,
                        statusMessage: MISSING_REQUIRED_INFORMATION,
                    };
                }

                // Run custom validation if provided
                if (fieldConfig.validate) {
                    return fieldConfig.validate(value, allValues);
                }
            }

            return { isValid: true };
        },
        [isFormDirty]
    );

    // Validate all fields in the form
    const validateForm = useCallback(
        (currentValues: T): ValidationResult => {
            const fieldResults: FieldValidationResults<T> = {};

            // Validate individual fields and collect results
            for (const fieldConfig of formConfig.fields) {
                const fieldValue = currentValues[fieldConfig.name];
                const fieldResult = validateField(fieldConfig, fieldValue, currentValues);
                fieldResults[fieldConfig.name] = fieldResult;

                // Don't stop at first invalid field - validate all fields
                // to give `validateForm` access to all field validation states
            }

            // Default behavior: the form is invalid if any individual field is invalid
            const firstInvalidField = Object.entries(fieldResults).find(
                ([_, result]) => !(result as ValidationResult).isValid
            );

            if (firstInvalidField) {
                return firstInvalidField[1] as ValidationResult;
            }

            // Check the form-level validation in case there are special
            // conditions involving dependencies between specific fields.
            let formLevelValidation: ValidationResult = { isValid: true };
            if (formConfig.validateForm) {
                formLevelValidation = formConfig.validateForm(currentValues, fieldResults);
            }

            return formLevelValidation;
        },
        [formConfig, validateField]
    );

    const isFieldValid = useCallback(<K extends keyof T>(fieldName: K): boolean => {
        const fieldConfig = formConfig.fields.find(field => field.name === fieldName);
        if (!fieldConfig) {
            return true; // No validation for this field
        }
        const fieldValue = formValues[fieldName];
        const validationResult = validateField(fieldConfig, fieldValue, formValues);
        return validationResult.isValid;
    }, [formValues, formConfig, validateField]);

    const setField = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
        setIsFormDirty(true);
        setFormValues(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const resetForm = useCallback(() => {
        setFormValues(initialValues);
        setFormStatusMessage(initialFormStatusMessage);
        setIsFormDirty(false);
    }, [initialValues, initialFormStatusMessage]);

    const isFormValid = useMemo(() => {
        const result = validateForm(formValues);
        return result.isValid;
    }, [formValues, validateForm]);

    useEffect(() => {
        setFormValues(initialValues);
        setFormStatusMessage(initialFormStatusMessage);
    }, [initialValues, initialFormStatusMessage]);

    // Update status message when form state changes
    useEffect(() => {
        if (isFormDirty) {
            const result = validateForm(formValues);
            if (!result.isValid && result.statusMessage) {
                setFormStatusMessage(result.statusMessage);
            } else {
                setFormStatusMessage("");
            }
        }
    }, [formValues, isFormDirty, validateForm]);

    // Handle form submission status
    useEffect(() => {
        const { loading, called, error, successMessage, failureMessage, onSuccess, successDescription } = formConfig.submitOperationConfig;
        if (!loading && called) {
            if (error) {
                toaster.warning(failureMessage, { duration: 5 });
                console.error("Form submission error:", error);
            } else {
                const hasSuccessDescription = successDescription && successDescription !== ""
                toaster.success(successMessage, {
                    description: hasSuccessDescription ? successDescription : null,
                    duration: 5,
                });
                resetForm();
                onSuccess?.();
            }
        }
    }, [formConfig.submitOperationConfig, resetForm]);

    return {
        formValues,
        setField,
        isFieldValid,
        resetForm,
        formStatusMessage,
        isFormValid,
        isFormDirty,
    };
}
